"use client";

import { useState } from "react";
import { PillGroup, SelectField } from "@/components/tool-kit/primitives";
import { FixedPointPanel } from "@/components/tools/chem-physical/colligative-fixed-panel";
import { MolarMassPanel } from "@/components/tools/chem-physical/colligative-molar-panel";
import { OsmoticPanel } from "@/components/tools/chem-physical/colligative-osmotic-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const COLLIGATIVE_PROPERTIES_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeFixed: "Boiling or freezing point",
  modeMolar: "Molar mass from ΔT",
  modeOsmotic: "Osmotic pressure",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  solveLabel: "Solve for",
  results: "Results",
  working: "The working",
  answerSuffix: "{label} (answer)",
  kb: "Kb",
  kf: "Kf",
  solventLabel: "Solvent",
  solventOption: "{name} ({constant} = {k} °C·kg/mol, {point} °C)",
  solventCustom: "Another solvent (enter its constant)",
  solventHint:
    "Constants are from OpenStax Chemistry 2e, Table 11.2, at 1 atm.",
  solvents: {
    water: "Water",
    aceticAcid: "Glacial acetic acid",
    benzene: "Benzene",
    chloroform: "Chloroform",
    nitrobenzene: "Nitrobenzene",
  },
  customK: "{constant} of the solvent (°C·kg/mol)",
  customKHint: "A property of the solvent alone; look it up for yours.",
  pureBoilingPoint: "Boiling point of the pure solvent",
  pureFreezingPoint: "Freezing point of the pure solvent",
  unitTemperature: "Temperature unit",
  termT: "Temperature (T)",
  soluteLabel: "Solute (fills in i)",
  soluteCustom: "Enter my own i",
  soluteIdeal: "{formula}, ideal i = {i}",
  soluteMeasured: "{formula}, measured at 0.050 m, i = {i}",
  soluteHint:
    'Quick fills from OpenStax Chemistry 2e, Table 11.3. Pick "measured" for a closer match to real salt solutions.',
  vantHoff: "van 't Hoff factor (i)",
  iHint:
    "Moles of particles per mole of solute: 1 for sugar, 2 for NaCl, 3 for CaCl₂. Association in the solvent can give values below 1.",
  propertyLabel: "Property",
  propertyFreeze: "Freezing-point depression",
  propertyBoil: "Boiling-point elevation",
  methodLabel: "Concentration given as",
  methodMolality: "Molality",
  methodMass: "Masses of solute and solvent",
  molality: "Molality (m)",
  molalityHint:
    "Moles of solute per kilogram of solvent, not per litre of solution.",
  soluteMass: "Mass of solute",
  solventMass: "Mass of solvent",
  soluteMolarMass: "Molar mass of solute (g/mol)",
  molarMassHint: "From the formula, or use the Molar Mass Calculator.",
  unitMass: "Mass unit",
  resultsIn: "Show the result in",
  solutionBoilingPoint: "Boiling point of the solution",
  solutionFreezingPoint: "Freezing point of the solution",
  elevation: "Boiling-point elevation (ΔTb)",
  depression: "Freezing-point depression (ΔTf)",
  measuredNote:
    'Real electrolytes show a slightly smaller shift than the ideal i predicts, because ions attract one another: NaCl measures about i = 1.9 at 0.050 m, not 2. Use a "measured" value for a closer estimate.',
  equationBoil: "ΔTb = i × Kb × m,  boiling point = pure boiling point + ΔTb",
  equationFreeze:
    "ΔTf = i × Kf × m,  freezing point = pure freezing point − ΔTf",
  convertLine: "{label}: {value} {unit} = {converted}",
  molesLine: "n = {mass} g ÷ {molar} g/mol = {moles} mol",
  molalityLine: "m = {moles} mol ÷ {kg} kg = {molality} mol/kg",
  shiftBoil: "ΔTb = {i} × {k} × {m} = {delta} °C",
  shiftFreeze: "ΔTf = {i} × {k} × {m} = {delta} °C",
  pointBoil: "Boiling point = {pure} °C + {delta} °C = {result}",
  pointFreeze: "Freezing point = {pure} °C − {delta} °C = {result}",
  answerLine: "{result} = {answer}",
  observedBoilingPoint: "Observed boiling point of the solution",
  observedFreezingPoint: "Observed freezing point of the solution",
  molarMassResult: "Molar mass of the solute",
  molarEquation:
    "M = mass of solute ÷ moles, where moles = ΔT ÷ (i × K) × kg of solvent",
  molarDeltaBoil: "ΔTb = {observed} °C − {pure} °C = {delta} °C",
  molarDeltaFreeze: "ΔTf = {pure} °C − {observed} °C = {delta} °C",
  molarMolality: "m = ΔT ÷ (i × K) = {delta} ÷ ({i} × {k}) = {molality} mol/kg",
  molarMoles: "n = m × kg of solvent = {molality} × {kg} = {moles} mol",
  molarResult: "M = {mass} g ÷ {moles} mol = {result}",
  osmoticPressure: "Osmotic pressure (Π)",
  osmoticConcentration: "Solute concentration (M)",
  solutionVolume: "Volume of solution",
  unitPressure: "Pressure unit",
  unitConcentration: "Concentration unit",
  unitVolume: "Volume unit",
  osmoticEquation: "Π = i × M × R × T",
  osmoticRearrangedPi: "Π = i × M × R × T",
  osmoticRearrangedM: "M = Π ÷ (i × R × T)",
  osmoticRearrangedMm:
    "M = Π ÷ (i × R × T), then molar mass = mass ÷ (M × volume)",
  concentrationLine: "M = {molar} mol/L = {cubic} mol/m³",
  osmoticSubstitutedPi:
    "Π = {i} × {c} mol/m³ × {r} J/(mol·K) × {t} K = {pi} Pa",
  osmoticSubstitutedM:
    "M = {pi} Pa ÷ ({i} × {r} J/(mol·K) × {t} K) = {c} mol/m³ = {molar} mol/L",
  osmoticSubstitutedMm:
    "M = {pi} Pa ÷ ({i} × {r} J/(mol·K) × {t} K) = {molar} mol/L (×1000 for mol/m³)",
  osmoticMolesLine: "n = M × V = {molar} mol/L × {vol} L = {moles} mol",
  osmoticMolarMassLine: "Molar mass = {mass} g ÷ {moles} mol = {result} g/mol",
  rLine:
    "R = {r} J/(mol·K) = {rAtm} L·atm/(mol·K), exact since the 2019 SI redefinition.",
  enterValues: "Enter the remaining values to see the answer.",
  errorUnreadable:
    "{label} is not a number. Use digits, such as 2.50 or 1.5e3.",
  errorNonPositive: "{label} must be greater than zero.",
  errorBelowZero:
    "{label} is at or below absolute zero (0 K, −273.15 °C, −459.67 °F), which is impossible.",
  errorResultBelowZero:
    "The result would be below absolute zero. Check the molality and the solvent constant.",
  errorWrongDirection:
    "A solute raises the boiling point and lowers the freezing point. The observed temperature must be above the pure solvent's for boiling, and below it for freezing.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value}",
} as const;

type Mode = "fixed" | "molar" | "osmotic";
const SIG_CHOICES = ["3", "4", "5", "6"] as const;

export default function ColligativePropertiesCalculatorWidget() {
  const strings = useToolUi(COLLIGATIVE_PROPERTIES_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("fixed");
  const [sigChoice, setSigChoice] = useState("match");
  const panels = {
    fixed: <FixedPointPanel sigChoice={sigChoice} strings={strings} />,
    molar: <MolarMassPanel sigChoice={sigChoice} strings={strings} />,
    osmotic: <OsmoticPanel sigChoice={sigChoice} strings={strings} />,
  };
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="col-mode"
        onChange={setMode}
        options={[
          { value: "fixed", label: strings.modeFixed },
          { value: "molar", label: strings.modeMolar },
          { value: "osmotic", label: strings.modeOsmotic },
        ]}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="col-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      {panels[mode]}
    </>
  );
}
